import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import ResponsiveHeader from '../components/ResponsiveHeader';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const CalorieCalculatorPage = () => {
  const [formData, setFormData] = useState({
    weight: '',
    height: '',
    age: '',
    gender: 'male',
    activity_level: 'moderately_active'
  });
  
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mealPlanError, setMealPlanError] = useState('');
  const [mealPlanNeedsLogin, setMealPlanNeedsLogin] = useState(false);
  
  // Meal plan states
  const [selectedCalorie, setSelectedCalorie] = useState(null);
  const [selectedProteinMultiplier, setSelectedProteinMultiplier] = useState(1.6);
  const [mealPlan, setMealPlan] = useState(null);
  const [loadingMealPlan, setLoadingMealPlan] = useState(false);

  const activityLevels = [
    { value: 'sedentary', label: 'Sedentary', description: 'Little or no exercise' },
    { value: 'lightly_active', label: 'Lightly Active', description: 'Light exercise 1-3 days/week' },
    { value: 'moderately_active', label: 'Moderately Active', description: 'Moderate exercise 3-5 days/week' },
    { value: 'very_active', label: 'Very Active', description: 'Hard exercise 6-7 days/week' },
    { value: 'extra_active', label: 'Extra Active', description: 'Very hard exercise & physical job' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.weight || !formData.height || !formData.age) {
      setError('Please fill in all fields');
      return false;
    }
    
    if (formData.weight <= 0 || formData.height <= 0 || formData.age <= 0) {
      setError('Please enter valid positive numbers');
      return false;
    }
    
    if (formData.age > 120) {
      setError('Please enter a valid age');
      return false;
    }
    
    return true;
  };

  const handleCalculate = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const response = await fetch(`${API_BASE}/api/calculator/calories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          weight: parseFloat(formData.weight),
          height: parseFloat(formData.height),
          age: parseInt(formData.age),
          gender: formData.gender,
          activity_level: formData.activity_level
        })
      });
      
      if (!response.ok) {
        throw new Error('Calculation failed');
      }
      
      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError('Failed to calculate. Please try again.');
      console.error('Calculation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMealPlan = async () => {
    if (!selectedCalorie || !formData.weight) {
      setError('Please calculate calories first and select a target');
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      setMealPlanNeedsLogin(true);
      setMealPlanError('Please log in to create your personalized meal plan.');
      return;
    }
    
    setLoadingMealPlan(true);
    setMealPlanError('');
    setMealPlanNeedsLogin(false);
    
    try {
      const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const response = await fetch(`${API_BASE}/api/calculator/meal-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          target_calories: selectedCalorie,
          weight: parseFloat(formData.weight),
          protein_multiplier: selectedProteinMultiplier
        })
      });
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setMealPlanError('Your session has expired. Please log in again to create a meal plan.');
        } else if (response.status === 429) {
          setMealPlanError("You have reached today's meal plan limit. Please try again tomorrow.");
        } else if (response.status === 503) {
          setMealPlanError('Meal plan generation is temporarily unavailable. Please try again later.');
        } else {
          setMealPlanError('Failed to generate meal plan. Please try again.');
        }
        return;
      }
      
      const data = await response.json();
      setMealPlan(data);
    } catch (err) {
      setMealPlanError('Failed to generate meal plan. Please try again.');
      console.error('Meal plan error:', err);
    } finally {
      setLoadingMealPlan(false);
    }
  };

  const resultsData = results ? [
    {
      icon: Minus,
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      label: 'Maintain weight',
      calories: results.maintain_weight,
      description: '100%',
      textColor: 'text-blue-900'
    },
    {
      icon: TrendingDown,
      iconColor: 'text-green-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      label: 'Mild weight loss',
      calories: results.mild_weight_loss,
      description: '0.25 kg/week',
      textColor: 'text-green-900'
    },
    {
      icon: TrendingDown,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-300',
      label: 'Weight loss',
      calories: results.weight_loss,
      description: '0.5 kg/week',
      textColor: 'text-green-900'
    },
    {
      icon: TrendingDown,
      iconColor: 'text-green-700',
      bgColor: 'bg-green-200',
      borderColor: 'border-green-400',
      label: 'Extreme weight loss',
      calories: results.extreme_weight_loss,
      description: '1 kg/week',
      textColor: 'text-green-900'
    },
    {
      icon: TrendingUp,
      iconColor: 'text-orange-500',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      label: 'Mild weight gain',
      calories: results.mild_weight_gain,
      description: '0.25 kg/week',
      textColor: 'text-orange-900'
    },
    {
      icon: TrendingUp,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-100',
      borderColor: 'border-orange-300',
      label: 'Weight gain',
      calories: results.weight_gain,
      description: '0.5 kg/week',
      textColor: 'text-orange-900'
    },
    {
      icon: TrendingUp,
      iconColor: 'text-orange-700',
      bgColor: 'bg-orange-200',
      borderColor: 'border-orange-400',
      label: 'Fast weight gain',
      calories: results.fast_weight_gain,
      description: '1 kg/week',
      textColor: 'text-orange-900'
    }
  ] : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <ResponsiveHeader />
      
      <div className="max-w-6xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-red-600 to-red-800 mb-6 shadow-lg">
            <Calculator className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-gray-900 dark:text-white mb-4">
            Calorie <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800">Calculator</span>
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Calculate your daily calorie needs based on the Mifflin-St Jeor formula. Get personalized recommendations for your fitness goals.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 border-2 border-red-100 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center text-red-700 dark:text-red-400 font-bold">1</span>
              Your Information
            </h2>
            
            <div className="space-y-6">
              {/* Weight */}
              <div>
                <Label htmlFor="weight" className="text-gray-700 dark:text-gray-300 font-semibold mb-2 block">
                  Weight (kg)
                </Label>
                <Input
                  id="weight"
                  name="weight"
                  type="number"
                  value={formData.weight}
                  onChange={handleInputChange}
                  placeholder="e.g., 83"
                  className="w-full text-lg border-2 border-gray-200 dark:border-gray-600 focus:border-red-600 dark:focus:border-purple-400 rounded-xl"
                  min="1"
                  step="0.1"
                />
              </div>

              {/* Height */}
              <div>
                <Label htmlFor="height" className="text-gray-700 dark:text-gray-300 font-semibold mb-2 block">
                  Height (cm)
                </Label>
                <Input
                  id="height"
                  name="height"
                  type="number"
                  value={formData.height}
                  onChange={handleInputChange}
                  placeholder="e.g., 185"
                  className="w-full text-lg border-2 border-gray-200 dark:border-gray-600 focus:border-red-600 dark:focus:border-purple-400 rounded-xl"
                  min="1"
                  step="0.1"
                />
              </div>

              {/* Age */}
              <div>
                <Label htmlFor="age" className="text-gray-700 dark:text-gray-300 font-semibold mb-2 block">
                  Age (years)
                </Label>
                <Input
                  id="age"
                  name="age"
                  type="number"
                  value={formData.age}
                  onChange={handleInputChange}
                  placeholder="e.g., 23"
                  className="w-full text-lg border-2 border-gray-200 dark:border-gray-600 focus:border-red-600 dark:focus:border-purple-400 rounded-xl"
                  min="1"
                  max="120"
                />
              </div>

              {/* Gender */}
              <div>
                <Label className="text-gray-700 dark:text-gray-300 font-semibold mb-3 block">
                  Gender
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => handleInputChange({ target: { name: 'gender', value: 'male' } })}
                    className={`p-4 rounded-xl border-2 font-semibold transition-all ${
                      formData.gender === 'male'
                        ? 'border-red-600 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 shadow-lg'
                        : 'border-gray-200 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    Male
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange({ target: { name: 'gender', value: 'female' } })}
                    className={`p-4 rounded-xl border-2 font-semibold transition-all ${
                      formData.gender === 'female'
                        ? 'border-red-600 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 shadow-lg'
                        : 'border-gray-200 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-600 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    Female
                  </button>
                </div>
              </div>

              {/* Activity Level */}
              <div>
                <Label className="text-gray-700 dark:text-gray-300 font-semibold mb-3 block">
                  Activity Level
                </Label>
                <div className="space-y-2">
                  {activityLevels.map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => handleInputChange({ target: { name: 'activity_level', value: level.value } })}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        formData.activity_level === level.value
                          ? 'border-red-600 bg-red-50 dark:bg-red-900/30 shadow-lg'
                          : 'border-gray-200 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-600'
                      }`}
                    >
                      <div className="font-semibold text-gray-900 dark:text-white">{level.label}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{level.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/30 border-2 border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 font-semibold">
                  {error}
                </div>
              )}

              {/* Calculate Button */}
              <Button
                onClick={handleCalculate}
                disabled={loading}
                className="w-full py-6 text-lg font-bold bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-800 text-white rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Calculating...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Calculate Calories
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Results Panel */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 border-2 border-red-100 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center text-red-700 dark:text-red-400 font-bold">2</span>
              Your Results
            </h2>

            {!results ? (
              <div className="flex flex-col items-center justify-center h-96 text-center">
                <div className="w-24 h-24 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-6">
                  <Calculator className="w-12 h-12 text-red-400 dark:text-red-500" />
                </div>
                <p className="text-lg text-gray-600 dark:text-gray-400 font-medium">
                  Enter your information and click<br />
                  "Calculate Calories" to see your results
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* BMR & TDEE Info */}
                <div className="bg-gradient-to-r from-red-100 to-gray-200 dark:from-purple-900/30 dark:to-pink-900/30 rounded-2xl p-6 mb-6">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">BMR</p>
                      <p className="text-2xl font-black text-red-700 dark:text-red-400">{Math.round(results.bmr)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">Basal Metabolic Rate</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">TDEE</p>
                      <p className="text-2xl font-black text-red-700 dark:text-pink-400">{Math.round(results.tdee)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500">Daily Energy Expenditure</p>
                    </div>
                  </div>
                </div>

                {/* Results Cards */}
                <div className="space-y-3">
                  {resultsData.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={index}
                        className={`${selectedCalorie === item.calories ? 'ring-4 ring-purple-500' : ''} ${item.bgColor} dark:bg-gray-700/50 border-2 ${item.borderColor} dark:border-gray-600 rounded-xl p-4 transition-all hover:shadow-lg cursor-pointer`}
                        onClick={() => setSelectedCalorie(item.calories)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full ${item.bgColor} dark:bg-gray-600 flex items-center justify-center`}>
                              <Icon className={`w-5 h-5 ${item.iconColor}`} />
                            </div>
                            <div>
                              <p className={`font-bold ${item.textColor} dark:text-white capitalize`}>
                                {item.label}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{item.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className={`text-2xl font-black ${item.textColor} dark:text-white`}>
                                {item.calories}
                              </p>
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">cal/day</p>
                            </div>
                            {selectedCalorie === item.calories && (
                              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Disclaimer */}
                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-200 dark:border-gray-600">
                  <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    <strong className="text-gray-900 dark:text-white">Note:</strong> These calculations are estimates based on the Mifflin-St Jeor formula. 
                    Individual needs may vary. Consult with a healthcare professional or certified nutritionist for personalized advice.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Meal Plan Section */}
        {results && (
          <div className="mt-12 bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 border-2 border-red-100 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center text-red-700 dark:text-red-400 font-bold">3</span>
              Create Your Meal Plan
            </h2>

            {!selectedCalorie ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                  </svg>
                </div>
                <p className="text-lg text-gray-600 dark:text-gray-400 font-medium">
                  Select a calorie target above to create your personalized meal plan
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Selected Target Info */}
                <div className="bg-gradient-to-r from-red-100 to-gray-200 dark:from-purple-900/30 dark:to-pink-900/30 rounded-2xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Selected Target</p>
                      <p className="text-3xl font-black text-red-700 dark:text-red-400">{selectedCalorie} cal/day</p>
                    </div>
                    <Button
                      onClick={() => { setSelectedCalorie(null); setMealPlan(null); }}
                      variant="outline"
                      className="text-sm"
                    >
                      Change Target
                    </Button>
                  </div>
                </div>

                {/* Protein Selection */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-300 font-semibold mb-3 block">
                    Select Protein Level
                  </Label>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { value: 1.0, label: 'Standard', formula: '1x weight' },
                      { value: 1.6, label: 'Active', formula: '1.6x weight' },
                      { value: 2.4, label: 'Athlete', formula: '2.4x weight' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setSelectedProteinMultiplier(option.value)}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          selectedProteinMultiplier === option.value
                            ? 'border-red-600 bg-red-50 dark:bg-red-900/30 shadow-lg'
                            : 'border-gray-200 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-600'
                        }`}
                      >
                        <div className="font-bold text-gray-900 dark:text-white">{option.label}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{option.formula}</div>
                        <div className="text-xs text-red-700 dark:text-red-400 mt-1">
                          {Math.round(parseFloat(formData.weight) * option.value)}g protein
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <Button
                  onClick={handleGenerateMealPlan}
                  disabled={loadingMealPlan}
                  className="w-full py-6 text-lg font-bold bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-800 text-white rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                >
                  {loadingMealPlan ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Generating Your Meal Plan...
                    </span>
                  ) : (
                    <span>Create Meal Plan For Me</span>
                  )}
                </Button>

                {/* Meal Plan Message (shown under the button) */}
                {mealPlanError && (
                  <div data-testid="meal-plan-message" className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border-2 border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 font-semibold">
                    {mealPlanError}
                    {mealPlanNeedsLogin && (
                      <Link to="/login" data-testid="meal-plan-login-link" className="ml-2 underline text-red-800 dark:text-red-200">Log in</Link>
                    )}
                  </div>
                )}

                {/* Meal Plan Results */}
                {mealPlan && (
                  <div className="mt-8 space-y-6">
                    <div className="bg-gradient-to-r from-green-100 to-blue-100 dark:from-green-900/30 dark:to-blue-900/30 rounded-2xl p-4 text-center">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Target Protein: <span className="text-2xl font-black text-green-700 dark:text-green-400">{mealPlan.target_protein_grams}g</span>
                      </p>
                    </div>

                    {mealPlan.variants.map((variant, idx) => (
                      <div key={idx} className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-6 border-2 border-gray-200 dark:border-gray-600">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                            {variant.protein_type}-Based Plan
                          </h3>
                          <div className="text-right">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Total Calories</p>
                            <p className="text-2xl font-black text-red-700 dark:text-red-400">
                              {variant.total_calories}
                            </p>
                          </div>
                        </div>

                        {/* Macro Summary */}
                        <div className="grid grid-cols-3 gap-3 mb-6">
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-xl p-3 text-center border border-blue-200 dark:border-blue-700">
                            <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">PROTEIN</p>
                            <p className="text-2xl font-black text-blue-700 dark:text-blue-300">{variant.total_protein}g</p>
                          </div>
                          <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/30 rounded-xl p-3 text-center border border-orange-200 dark:border-orange-700">
                            <p className="text-xs font-semibold text-orange-600 dark:text-orange-400 mb-1">CARBS</p>
                            <p className="text-2xl font-black text-orange-700 dark:text-orange-300">{variant.total_carbs}g</p>
                          </div>
                          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/30 dark:to-yellow-800/30 rounded-xl p-3 text-center border border-yellow-200 dark:border-yellow-700">
                            <p className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 mb-1">FATS</p>
                            <p className="text-2xl font-black text-yellow-700 dark:text-yellow-300">{variant.total_fats}g</p>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                            <p className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                              <span className="text-xl">🌅</span> Breakfast
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed">{variant.breakfast}</p>
                          </div>
                          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                            <p className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                              <span className="text-xl">🍽️</span> Lunch
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed">{variant.lunch}</p>
                          </div>
                          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                            <p className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                              <span className="text-xl">🌙</span> Dinner
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed">{variant.dinner}</p>
                          </div>
                          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                            <p className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                              <span className="text-xl">🍎</span> Snacks
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed">{variant.snacks}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CalorieCalculatorPage;
